import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  Switch,
  VStack,
  HStack,
  useToast,
  Box,
  Text,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { useApi } from '@/contexts/ApiContext';
import { EmailChannelConfig } from './EmailChannelConfig';
import { TelegramChannelConfig } from './TelegramChannelConfig';
import { WhatsAppChannelConfig } from './WhatsAppChannelConfig';

interface CreateChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ChannelFormData {
  name: string;
  type: string;
  description: string;
  priority: number;
  isEnabled: boolean;
  tags: string[];
  config: Record<string, any>;
}

export const CreateChannelModal: React.FC<CreateChannelModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const api = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');

  const [formData, setFormData] = useState<ChannelFormData>({
    name: '',
    type: 'email',
    description: '',
    priority: 5,
    isEnabled: true,
    tags: [],
    config: {},
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!formData.name.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Channel name is required',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate config based on channel type
      const configValidation = validateConfig(formData.type, formData.config);
      if (!configValidation.isValid) {
        toast({
          title: 'Configuration Error',
          description: configValidation.error,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      await api.NotificationsApi.createChannel({
        channel: {
          name: formData.name,
          description: formData.description,
          priority: formData.priority,
          is_enabled: formData.isEnabled,
          tags: formData.tags,
          // Add channel config based on type
          ...(formData.type === 'email' && { email_config: formData.config }),
          ...(formData.type === 'telegram' && { telegram_config: formData.config }),
          ...(formData.type === 'whatsapp' && { whatsapp_config: formData.config }),
        }
      });

      toast({
        title: 'Success',
        description: 'Channel created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Failed to create channel:', error);
      toast({
        title: 'Error',
        description: 'Failed to create channel',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      type: 'email',
      description: '',
      priority: 5,
      isEnabled: true,
      tags: [],
      config: {},
    });
    setNewTag('');
    onClose();
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleConfigChange = (config: Record<string, any>) => {
    setFormData(prev => ({
      ...prev,
      config
    }));
  };

  const validateConfig = (type: string, config: Record<string, any>) => {
    switch (type) {
      case 'email':
        if (!config.host) return { isValid: false, error: 'SMTP host is required' };
        if (!config.port) return { isValid: false, error: 'SMTP port is required' };
        if (!config.fromEmail) return { isValid: false, error: 'From email is required' };
        break;
      case 'telegram':
        if (!config.botToken) return { isValid: false, error: 'Bot token is required' };
        break;
      case 'whatsapp':
        if (!config.accessToken) return { isValid: false, error: 'Access token is required' };
        if (!config.phoneNumberId) return { isValid: false, error: 'Phone number ID is required' };
        break;
    }
    return { isValid: true };
  };

  const renderConfigForm = () => {
    switch (formData.type) {
      case 'email':
        return (
          <EmailChannelConfig
            config={formData.config}
            onChange={handleConfigChange}
          />
        );
      case 'telegram':
        return (
          <TelegramChannelConfig
            config={formData.config}
            onChange={handleConfigChange}
          />
        );
      case 'whatsapp':
        return (
          <WhatsAppChannelConfig
            config={formData.config}
            onChange={handleConfigChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Notification Channel</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Basic Information */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Basic Information
              </Text>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Channel Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Primary Email Server"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Channel Type</FormLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      type: e.target.value,
                      config: {} // Reset config when type changes
                    }))}
                  >
                    <option value="email">Email (SMTP)</option>
                    <option value="telegram">Telegram</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms">SMS</option>
                    <option value="push">Push Notification</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description for this channel"
                    rows={2}
                  />
                </FormControl>

                <HStack spacing={4} width="100%">
                  <FormControl>
                    <FormLabel>Priority (1-10)</FormLabel>
                    <NumberInput
                      value={formData.priority}
                      onChange={(_, value) => setFormData(prev => ({ ...prev, priority: value || 5 }))}
                      min={1}
                      max={10}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Enabled</FormLabel>
                    <Switch
                      isChecked={formData.isEnabled}
                      onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                      colorScheme="primary"
                    />
                  </FormControl>
                </HStack>

                {/* Tags */}
                <FormControl>
                  <FormLabel>Tags</FormLabel>
                  <HStack>
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add tag"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    <Button onClick={handleAddTag} size="sm">
                      Add
                    </Button>
                  </HStack>
                  {formData.tags.length > 0 && (
                    <Wrap mt={2}>
                      {formData.tags.map((tag) => (
                        <WrapItem key={tag}>
                          <Tag size="md" variant="solid" colorScheme="primary">
                            <TagLabel>{tag}</TagLabel>
                            <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Channel Configuration */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3}>
                Channel Configuration
              </Text>
              {renderConfigForm()}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
          >
            Create Channel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};